/**
 * /automation-accounts/$id — platform-scoped automation-account detail route.
 *
 * Prefetches the detail record via ensureQueryData so the page
 * doesn't flash a spinner for its primary data. Owns the literal
 * navigate targets + Link wrappers for the feature page so the
 * strict route typing stays intact.
 */

import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  fetchAutomationAccount,
  scopeKey,
} from "@/features/automation-accounts/queries";
import { queryKeys } from "@/lib/query-keys";
import { AutomationAccountDetailPage } from "@/features/automation-accounts/pages/automation-account-detail-page";

function PlatformAutomationAccountDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  return (
    <AutomationAccountDetailPage
      tenantID={null}
      id={id}
      scope="platform"
      basePath="/automation-accounts"
      onBackToList={() => void navigate({ to: "/automation-accounts" })}
      renderBackLink={({ children, className }) => (
        <Link to="/automation-accounts" className={className}>
          {children}
        </Link>
      )}
      // Deep-link into the platform audit page pre-filtered on
      // events where this account is the TARGET (resource_id) — the
      // "who touched this identity" forensic view. Actions
      // performed BY the account (using its token) are shown inline
      // via the per-account timeline on the detail page; operators
      // who need both can swap resource_id for actor_id on the
      // audit page's filter row.
      renderAuditLink={({ children }) => (
        <Link to="/audit" search={{ resource_id: id }}>
          {children}
        </Link>
      )}
    />
  );
}

export const Route = createFileRoute("/_authed/automation-accounts/$id")({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData({
      queryKey: queryKeys.automationAccounts.detail(scopeKey(null), params.id),
      queryFn: () => fetchAutomationAccount(null, params.id),
    }),
  component: PlatformAutomationAccountDetailPage,
});
